import {
  Authentication,
  renderDataGridLocaleText,
  renderDatePickerLocaleText,
  renderAutoCompleteLocaleText,
} from 'utils';
import {
  Box,
  Tab,
  Tabs,
  Paper,
  useTheme,
  TextField,
  Typography,
  Autocomplete,
  Grid2 as Grid,
  svgIconClasses,
} from '@mui/material';
import {
  prepareTab3Books,
  Tab3BookSummaryRow,
  buildTab3ChartData,
  Tab3ActivityTypeRow,
  calculateTab3GrandTotal,
  buildTab3BookSummaryRows,
} from './tab3-summary';
import dayjs, { Dayjs } from 'dayjs';
import apiService from 'library/api-service';
import { useAppSelector } from 'store/hooks';
import { useDataGridLocaleText } from 'hooks';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers';
import { BarChart } from '@mui/x-charts/BarChart';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import { STATUS_TYPE_ENUM, STORAGE_KEY_ENUM } from 'enums';
import { GridColDef, GridRowParams } from '@mui/x-data-grid-premium';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { CustomToolbar, ZebraDataGrid, CustomNoRowsOverlay } from 'components';
import { ActivityBoardItem, ActivityType, HolidayItem, ShortBook, User } from 'library/types';
import { useCallback, useEffect, useMemo, useState, SyntheticEvent, ReactNode, useLayoutEffect } from 'react';

// Types
type UserActivityRow = {
  activityTypeId: number;
  activityTypeTitle: string;
  spentHours: number;
  overtimeHours: number;
  totalHours: number;
};

type UserSummaryRow = {
  id: number;
  nameSurname: string;
  spentHours: number;
  idleHours: number;
  overtimeHours: number;
  totalHours: number;
  activityRows: UserActivityRow[];
};

type ActivityTypeUserRow = {
  userId: number;
  nameSurname: string;
  regularHours: number;
  overtimeHours: number;
  totalHours: number;
};

type ActivityTypeSummaryRow = {
  activityTypeId: number;
  activityTypeTitle: string;
  regularHours: number;
  overtimeHours: number;
  totalHours: number;
  userRows: ActivityTypeUserRow[];
};

// Helper: calculate expected working hours between two dates (inclusive), accounting for weekends and holidays
const calcExpectedHours = (start: Dayjs, end: Dayjs, holidays: HolidayItem[]): number => {
  let totalHours = 0;
  let current = start.startOf('day');
  const endDay = end.startOf('day');

  while (current.isBefore(endDay) || current.isSame(endDay, 'day')) {
    const dayOfWeek = current.day();

    // Skip weekends
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      const dateStr = current.format('DD.MM.YYYY');
      const holiday = holidays.find((h) => h.localeDateString === dateStr);

      if (holiday && !holiday.isHalfDay) {
        // Full-day holiday: 0 expected hours
      } else if (holiday && holiday.isHalfDay) {
        // Half-day holiday: 09:00-13:00 = 4 hours
        totalHours += 4;
      } else {
        // Normal weekday: 7.5 hours
        totalHours += 7.5;
      }
    }

    current = current.add(1, 'day');
  }

  return totalHours;
};

// Tab Panel component
type TabPanelProps = {
  children?: ReactNode;
  index: number;
  value: number;
};

const TabPanel = ({ children, value, index }: TabPanelProps) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const ActivityMonitoring = () => {
  const intl = useIntl();
  const theme = useTheme();
  const navigate = useNavigate();

  const dataGridLocaleText = useDataGridLocaleText();

  // redux hooks
  const permissionCodes = useAppSelector(selectPermissionCodes);
  const arePermissionsInitialized = permissionCodes[0] !== STORAGE_KEY_ENUM.NON_INITIALIZED;
  const canListActivityBoardItemsAsUser = permissionCodes.includes('list-activity-board-item');
  const canListActivityBoardItemsAsAdmin = permissionCodes.includes('list-activity-board-item-admin');
  const isActivityBoardItemUserScope = canListActivityBoardItemsAsUser && !canListActivityBoardItemsAsAdmin;

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Data states
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [books, setBooks] = useState<ShortBook[]>([]);
  const [holidays, setHolidays] = useState<HolidayItem[]>([]);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [activityBoardItems, setActivityBoardItems] = useState<ActivityBoardItem[]>([]);

  // Tab 1 Filter states
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));

  // Tab 2 Filter states
  const [tab2Loading, setTab2Loading] = useState(false);
  const [tab2EndDate, setTab2EndDate] = useState<Dayjs | null>(dayjs());
  const [tab2SelectedUser, setTab2SelectedUser] = useState<User | null>(null);
  const [tab2StartDate, setTab2StartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [tab2ActivityBoardItems, setTab2ActivityBoardItems] = useState<ActivityBoardItem[]>([]);

  // Tab 3 Filter states
  const [tab3Loading, setTab3Loading] = useState(false);
  const [tab3EndDate, setTab3EndDate] = useState<Dayjs | null>(dayjs());
  const [tab3SelectedBook, setTab3SelectedBook] = useState<ShortBook | null>(null);
  const [tab3SelectedUser, setTab3SelectedUser] = useState<User | null>(null);
  const [tab3StartDate, setTab3StartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [tab3ActivityBoardItems, setTab3ActivityBoardItems] = useState<ActivityBoardItem[]>([]);

  const loggedUserName = Authentication.getLoggedUser();
  const currentUser = useMemo(
    () => users.find((user) => user.nameSurname === loggedUserName) ?? null,
    [users, loggedUserName]
  );
  const tab1SelectedUser = isActivityBoardItemUserScope ? currentUser : selectedUser;
  const tab1SelectedUserId = tab1SelectedUser?.id ?? null;
  const tab3SelectedUserId = isActivityBoardItemUserScope ? (currentUser?.id ?? null) : (tab3SelectedUser?.id ?? null);

  // Tab change handler
  const handleTabChange = (_: SyntheticEvent, newValue: number) => {
    if (isActivityBoardItemUserScope && newValue !== 0 && newValue !== 2) return;

    setTabValue(newValue);
  };

  // Fetch users
  const getUsers = async () => {
    try {
      const response = await apiService.getUsers();

      if (response.isSuccess) {
        const formattedData = response.data
          .filter((user) => user.status === STATUS_TYPE_ENUM.ACTIVE)
          .sort((a, b) => (a.nameSurname || '').localeCompare(b.nameSurname || ''));
        setUsers(formattedData);
      }
    } catch {}
  };

  // Fetch activity types
  const getActivityTypes = async () => {
    try {
      const response = await apiService.getActivityTypes();

      if (response.isSuccess) {
        setActivityTypes(response.data);
      }
    } catch {}
  };

  // Fetch books (companies)
  const getBooks = async () => {
    try {
      const response = await apiService.getCompanies();
      if (response.isSuccess) {
        setBooks(prepareTab3Books(response.data));
      }
    } catch {}
  };

  // Fetch activity board items
  const getActivityBoardItems = useCallback(async () => {
    if (!arePermissionsInitialized || (isActivityBoardItemUserScope && !tab1SelectedUserId)) {
      setActivityBoardItems([]);
      return;
    }

    try {
      setLoading(true);

      const response = await apiService.getActivityBoardItems({
        bookId: null,
        projectId: null,
        userId: tab1SelectedUserId,
        startDate: startDate ? startDate.startOf('day').toISOString() : null,
        endDate: endDate ? endDate.endOf('day').toISOString() : null,
      });

      if (response.isSuccess) {
        setActivityBoardItems(response.data);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [arePermissionsInitialized, isActivityBoardItemUserScope, tab1SelectedUserId, startDate, endDate]);

  // Users filtered by isActivityUser flag
  const activityUsers = useMemo(() => users.filter((u) => u.isActivityUser), [users]);

  // Permission activity type IDs (isPermissionActivity === true)
  const permissionActivityTypeIds = useMemo(
    () => activityTypes.filter((at) => at.isPermissionActivity).map((at) => at.id),
    [activityTypes]
  );

  // Expected working hours for the selected date range
  const expectedHours = useMemo(
    () => (startDate && endDate ? calcExpectedHours(startDate, endDate, holidays) : 7.5),
    [startDate, endDate, holidays]
  );

  // Expected working hours for the selected date range (Tab 2)
  const tab2ExpectedHours = useMemo(
    () => (tab2StartDate && tab2EndDate ? calcExpectedHours(tab2StartDate, tab2EndDate, holidays) : 7.5),
    [tab2StartDate, tab2EndDate, holidays]
  );

  // Compute user summary rows
  const userSummaryRows: UserSummaryRow[] = useMemo(() => {
    // Group by user
    const userMap = new Map<
      number,
      {
        spentHours: number;
        overtimeHours: number;
        permissionHours: number;
        activityTypeMap: Map<number, { spentHours: number; overtimeHours: number }>;
      }
    >();

    for (const item of activityBoardItems) {
      if (isActivityBoardItemUserScope && (!currentUser || item.assignedUserId !== currentUser.id)) continue;

      const userId = item.assignedUserId;
      if (!userMap.has(userId)) {
        userMap.set(userId, {
          spentHours: 0,
          overtimeHours: 0,
          permissionHours: 0,
          activityTypeMap: new Map(),
        });
      }

      const entry = userMap.get(userId)!;
      const isPermission = permissionActivityTypeIds.includes(item.activityTypeId);

      if (isPermission) {
        // Permission activities are excluded from spentHours; track separately for expectedHours adjustment
        entry.permissionHours += item.estimatedHours;
      } else if (item.isOvertimeWork) {
        entry.overtimeHours += item.estimatedHours;
      } else {
        entry.spentHours += item.estimatedHours;
      }

      // Group activities by activityTypeId for sub-breakdown
      if (!entry.activityTypeMap.has(item.activityTypeId)) {
        entry.activityTypeMap.set(item.activityTypeId, { spentHours: 0, overtimeHours: 0 });
      }
      const atEntry = entry.activityTypeMap.get(item.activityTypeId)!;
      if (item.isOvertimeWork) {
        atEntry.overtimeHours += item.estimatedHours;
      } else {
        atEntry.spentHours += item.estimatedHours;
      }
    }

    // Build rows
    const rows: UserSummaryRow[] = [];

    userMap.forEach((value, userId) => {
      const user =
        activityUsers.find((u) => u.id === userId) ??
        (isActivityBoardItemUserScope && currentUser?.id === userId ? currentUser : undefined);
      if (!user) return;

      // Subtract permission hours from expected hours for this user
      const userExpectedHours = Math.max(0, expectedHours - value.permissionHours);
      const idleHours = Math.max(0, userExpectedHours - value.spentHours);

      const spentH = parseFloat(value.spentHours.toFixed(2));
      const overtimeH = parseFloat(value.overtimeHours.toFixed(2));

      // Build activity rows for details
      const activityRows: UserActivityRow[] = [];
      value.activityTypeMap.forEach((atVal, activityTypeId) => {
        const at = activityTypes.find((a) => a.id === activityTypeId);
        const spent = parseFloat(atVal.spentHours.toFixed(2));
        const overtime = parseFloat(atVal.overtimeHours.toFixed(2));

        activityRows.push({
          activityTypeId,
          activityTypeTitle: at?.title || `Activity Type #${activityTypeId}`,
          spentHours: spent,
          overtimeHours: overtime,
          totalHours: parseFloat((spent + overtime).toFixed(2)),
        });
      });

      // Sort by totalHours descending
      activityRows.sort((a, b) => b.totalHours - a.totalHours);

      const idleH = parseFloat(idleHours.toFixed(2));

      rows.push({
        id: userId,
        nameSurname: user.nameSurname,
        spentHours: spentH,
        idleHours: idleH,
        overtimeHours: overtimeH,
        totalHours: parseFloat((spentH + overtimeH).toFixed(2)),
        activityRows,
      });
    });

    // If filtering by null user, also include users with 0 activity
    const zeroActivityUsers = isActivityBoardItemUserScope
      ? currentUser
        ? [currentUser]
        : []
      : !selectedUser
        ? activityUsers
        : [];

    for (const user of zeroActivityUsers) {
      if (!userMap.has(user.id)) {
        const idleH = parseFloat(expectedHours.toFixed(2));

        rows.push({
          id: user.id,
          nameSurname: user.nameSurname,
          spentHours: 0,
          idleHours: idleH,
          overtimeHours: 0,
          totalHours: 0,
          activityRows: [],
        });
      }
    }

    return rows.sort((a, b) => b.spentHours - a.spentHours);
  }, [
    activityBoardItems,
    activityUsers,
    selectedUser,
    expectedHours,
    permissionActivityTypeIds,
    activityTypes,
    isActivityBoardItemUserScope,
    currentUser,
  ]);

  // Total spent hours
  const totalSpentHours = useMemo(
    () => parseFloat(userSummaryRows.reduce((sum, row) => sum + row.spentHours, 0).toFixed(2)),
    [userSummaryRows]
  );

  // Displayed expected hours: subtract permission activity hours from the selected scope
  const displayedExpectedHours = useMemo(() => {
    const totalPermissionHours = tab1SelectedUser
      ? activityBoardItems
          .filter(
            (item) =>
              item.assignedUserId === tab1SelectedUser.id && permissionActivityTypeIds.includes(item.activityTypeId)
          )
          .reduce((sum, item) => sum + item.estimatedHours, 0)
      : 0;
    return parseFloat(Math.max(0, expectedHours - totalPermissionHours).toFixed(2));
  }, [activityBoardItems, permissionActivityTypeIds, expectedHours, tab1SelectedUser]);

  // DataGrid columns
  const columns: GridColDef<UserSummaryRow>[] = useMemo(
    () => [
      {
        field: 'nameSurname',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.nameSurname',
          defaultMessage: 'Kullanıcı Adı',
        }),
        flex: 1,
        minWidth: 120,
      },
      {
        field: 'spentHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.spentHours',
          defaultMessage: 'Harcanan Süre (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 120,
      },
      {
        field: 'idleHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.idleHours',
          defaultMessage: 'Atıl Süre (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
      {
        field: 'overtimeHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.overtimeHours',
          defaultMessage: 'Fazla Mesai (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
      {
        field: 'totalHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.totalHours',
          defaultMessage: 'Toplam (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 90,
      },
    ],
    [intl]
  );

  // Tab 1: DataGrid columns for detail (activity type) rows
  const tab1DetailColumns: GridColDef<UserActivityRow>[] = useMemo(
    () => [
      {
        field: 'activityTypeTitle',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.activityType',
          defaultMessage: 'Aktivite Türü',
        }),
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'spentHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.spentHours',
          defaultMessage: 'Harcanan Süre (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 120,
      },
      {
        field: 'overtimeHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.overtimeHours',
          defaultMessage: 'Fazla Mesai (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
      {
        field: 'totalHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.totalHours',
          defaultMessage: 'Toplam (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
    ],
    [intl]
  );

  // Tab 1: Detail panel content (activity type breakdown per user)
  const getTab1DetailPanelContent = useCallback(
    (params: GridRowParams<UserSummaryRow>) => {
      const row = params.row;
      if (!row.activityRows || row.activityRows.length === 0) return null;

      return (
        <Box sx={{ p: 2 }}>
          <ZebraDataGrid<UserActivityRow>
            columns={tab1DetailColumns}
            rows={row.activityRows}
            getRowId={(r) => r.activityTypeId}
            density="compact"
            hideFooter
            disableColumnMenu
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnReorder
            disableColumnSorting
            localeText={dataGridLocaleText}
          />
        </Box>
      );
    },
    [tab1DetailColumns, dataGridLocaleText]
  );

  // Tab 1: Detail panel height
  const getTab1DetailPanelHeight = useCallback((params: GridRowParams<UserSummaryRow>) => {
    const count = params.row.activityRows?.length || 0;
    if (count === 0) return 0;
    return count * 36 + 56 + 16;
  }, []);

  // Chart data (only users with spentHours > 0)
  const chartData = useMemo(() => {
    const filtered = userSummaryRows.filter((row) => row.spentHours > 0);
    return {
      yLabels: filtered.map((row) => row.nameSurname),
      values: filtered.map((row) => row.spentHours),
    };
  }, [userSummaryRows]);

  // Tab 2: Fetch activity board items
  const getTab2ActivityBoardItems = useCallback(async () => {
    if (!arePermissionsInitialized || isActivityBoardItemUserScope) {
      setTab2ActivityBoardItems([]);
      return;
    }

    try {
      setTab2Loading(true);

      const response = await apiService.getActivityBoardItems({
        bookId: null,
        projectId: null,
        userId: tab2SelectedUser?.id || null,
        startDate: tab2StartDate ? tab2StartDate.startOf('day').toISOString() : null,
        endDate: tab2EndDate ? tab2EndDate.endOf('day').toISOString() : null,
      });

      if (response.isSuccess) {
        setTab2ActivityBoardItems(response.data);
      }
    } catch {
    } finally {
      setTab2Loading(false);
    }
  }, [arePermissionsInitialized, isActivityBoardItemUserScope, tab2SelectedUser, tab2StartDate, tab2EndDate]);

  // Tab 2: Compute activity type summary rows
  const activityTypeSummaryRows: ActivityTypeSummaryRow[] = useMemo(() => {
    // Build a map: activityTypeId -> userId -> { regularHours, overtimeHours }
    const typeMap = new Map<number, Map<number, { regularHours: number; overtimeHours: number }>>();

    for (const item of tab2ActivityBoardItems) {
      if (!typeMap.has(item.activityTypeId)) {
        typeMap.set(item.activityTypeId, new Map());
      }

      const userMap = typeMap.get(item.activityTypeId)!;
      if (!userMap.has(item.assignedUserId)) {
        userMap.set(item.assignedUserId, { regularHours: 0, overtimeHours: 0 });
      }

      const entry = userMap.get(item.assignedUserId)!;
      if (item.isOvertimeWork) {
        entry.overtimeHours += item.estimatedHours;
      } else {
        entry.regularHours += item.estimatedHours;
      }
    }

    // Build rows for ALL activity types (even those with 0 data)
    const rows: ActivityTypeSummaryRow[] = activityTypes.map((at) => {
      const userMap = typeMap.get(at.id);
      const userRows: ActivityTypeUserRow[] = [];

      if (userMap) {
        userMap.forEach((value, userId) => {
          const user = activityUsers.find((u) => u.id === userId);
          if (!user) return;

          userRows.push({
            userId,
            nameSurname: user.nameSurname,
            regularHours: parseFloat(value.regularHours.toFixed(2)),
            overtimeHours: parseFloat(value.overtimeHours.toFixed(2)),
            totalHours: parseFloat((value.regularHours + value.overtimeHours).toFixed(2)),
          });
        });
      }

      // Sort users by totalHours descending
      userRows.sort((a, b) => b.totalHours - a.totalHours);

      const regularHours = parseFloat(userRows.reduce((sum, r) => sum + r.regularHours, 0).toFixed(2));
      const overtimeHours = parseFloat(userRows.reduce((sum, r) => sum + r.overtimeHours, 0).toFixed(2));

      return {
        activityTypeId: at.id,
        activityTypeTitle: at.title,
        regularHours,
        overtimeHours,
        totalHours: parseFloat((regularHours + overtimeHours).toFixed(2)),
        userRows,
      };
    });

    // Build "Atıl Süre" (Idle Time) row manually
    const userHoursMap = new Map<number, { spentHours: number; permissionHours: number }>();
    const targetUsers = tab2SelectedUser ? (tab2SelectedUser.isActivityUser ? [tab2SelectedUser] : []) : activityUsers;

    for (const u of targetUsers) {
      userHoursMap.set(u.id, { spentHours: 0, permissionHours: 0 });
    }

    for (const item of tab2ActivityBoardItems) {
      const userId = item.assignedUserId;
      if (!userHoursMap.has(userId)) continue;

      const entry = userHoursMap.get(userId)!;
      const isPermission = permissionActivityTypeIds.includes(item.activityTypeId);

      if (isPermission) {
        entry.permissionHours += item.estimatedHours;
      } else if (!item.isOvertimeWork) {
        entry.spentHours += item.estimatedHours;
      }
    }

    const idleUserRows: ActivityTypeUserRow[] = [];
    userHoursMap.forEach((val, userId) => {
      const user = activityUsers.find((u) => u.id === userId);
      if (!user) return;

      const userExpected = Math.max(0, tab2ExpectedHours - val.permissionHours);
      const idleH = Math.max(0, userExpected - val.spentHours);

      idleUserRows.push({
        userId,
        nameSurname: user.nameSurname,
        regularHours: parseFloat(idleH.toFixed(2)),
        overtimeHours: 0,
        totalHours: parseFloat(idleH.toFixed(2)),
      });
    });

    // Sort users by idleHours (totalHours) descending
    idleUserRows.sort((a, b) => b.totalHours - a.totalHours);

    const idleRegularHours = parseFloat(idleUserRows.reduce((sum, r) => sum + r.regularHours, 0).toFixed(2));

    const idleSummaryRow: ActivityTypeSummaryRow = {
      activityTypeId: -2,
      activityTypeTitle: intl.formatMessage({
        id: 'activityMonitoring.idleHoursLabel',
        defaultMessage: 'Atıl Süre',
      }),
      regularHours: idleRegularHours,
      overtimeHours: 0,
      totalHours: idleRegularHours,
      userRows: idleUserRows,
    };

    rows.push(idleSummaryRow);

    // Sort by totalHours descending
    return rows.sort((a, b) => b.totalHours - a.totalHours);
  }, [
    tab2ActivityBoardItems,
    activityTypes,
    permissionActivityTypeIds,
    tab2ExpectedHours,
    tab2SelectedUser,
    intl,
    activityUsers,
  ]);

  // Tab 2: Grand total
  const tab2GrandTotal = useMemo(() => {
    const filteredRows = activityTypeSummaryRows.filter((r) => r.activityTypeId !== -2);
    const regularHours = parseFloat(filteredRows.reduce((sum, r) => sum + r.regularHours, 0).toFixed(2));
    const overtimeHours = parseFloat(filteredRows.reduce((sum, r) => sum + r.overtimeHours, 0).toFixed(2));
    return {
      regularHours,
      overtimeHours,
      totalHours: parseFloat((regularHours + overtimeHours).toFixed(2)),
    };
  }, [activityTypeSummaryRows]);

  // Tab 2: Chart data (only activity types with totalHours > 0, excluding Atıl Süre)
  const tab2ChartData = useMemo(() => {
    const filtered = activityTypeSummaryRows.filter((row) => row.activityTypeId !== -2 && row.totalHours > 0);
    return {
      yLabels: filtered.map((row) => row.activityTypeTitle),
      values: filtered.map((row) => row.totalHours),
    };
  }, [activityTypeSummaryRows]);

  // Tab 2: DataGrid columns for master rows
  const tab2Columns: GridColDef<ActivityTypeSummaryRow>[] = useMemo(
    () => [
      {
        field: 'activityTypeTitle',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.activityType',
          defaultMessage: 'Aktivite Türü',
        }),
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'regularHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.regularHours',
          defaultMessage: 'Mesai İçi (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
      {
        field: 'overtimeHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.overtimeHours',
          defaultMessage: 'Fazla Mesai (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
      {
        field: 'totalHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.totalHours',
          defaultMessage: 'Toplam (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
    ],
    [intl]
  );

  // Tab 2: DataGrid columns for detail (user) rows
  const tab2DetailColumns: GridColDef<ActivityTypeUserRow>[] = useMemo(
    () => [
      {
        field: 'nameSurname',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.nameSurname',
          defaultMessage: 'Kullanıcı Adı',
        }),
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'regularHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.regularHours',
          defaultMessage: 'Mesai İçi (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
      {
        field: 'overtimeHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.overtimeHours',
          defaultMessage: 'Fazla Mesai (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
      {
        field: 'totalHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.totalHours',
          defaultMessage: 'Toplam (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
    ],
    [intl]
  );

  // Tab 2: Detail panel content (user breakdown per activity type)
  const getTab2DetailPanelContent = useCallback(
    (params: GridRowParams<ActivityTypeSummaryRow>) => {
      const row = params.row;
      if (!row.userRows || row.userRows.length === 0) return null;

      return (
        <Box sx={{ p: 2 }}>
          <ZebraDataGrid<ActivityTypeUserRow>
            columns={tab2DetailColumns}
            rows={row.userRows}
            getRowId={(r) => r.userId}
            density="compact"
            hideFooter
            disableColumnMenu
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnReorder
            disableColumnSorting
            localeText={dataGridLocaleText}
          />
        </Box>
      );
    },
    [tab2DetailColumns, dataGridLocaleText]
  );

  // Tab 2: Detail panel height
  const getTab2DetailPanelHeight = useCallback((params: GridRowParams<ActivityTypeSummaryRow>) => {
    const userCount = params.row.userRows?.length || 0;
    if (userCount === 0) return 0;
    return userCount * 36 + 56 + 16;
  }, []);

  // Tab 2: Pinned rows (grand total at bottom)
  const tab2PinnedRows = useMemo(
    () => ({
      bottom: [
        {
          activityTypeId: -1,
          activityTypeTitle: intl.formatMessage({
            id: 'activityMonitoring.grandTotal',
            defaultMessage: 'Genel Toplam',
          }),
          regularHours: tab2GrandTotal.regularHours,
          overtimeHours: tab2GrandTotal.overtimeHours,
          totalHours: tab2GrandTotal.totalHours,
          userRows: [],
        } as ActivityTypeSummaryRow,
      ],
    }),
    [intl, tab2GrandTotal]
  );

  // Tab 3: Fetch activity board items
  const getTab3ActivityBoardItems = useCallback(async () => {
    if (!arePermissionsInitialized || (isActivityBoardItemUserScope && !tab3SelectedUserId)) {
      setTab3ActivityBoardItems([]);
      return;
    }

    try {
      setTab3Loading(true);

      const response = await apiService.getActivityBoardItems({
        bookId: tab3SelectedBook?.id || null,
        projectId: null,
        userId: tab3SelectedUserId,
        startDate: tab3StartDate ? tab3StartDate.startOf('day').toISOString() : null,
        endDate: tab3EndDate ? tab3EndDate.endOf('day').toISOString() : null,
      });

      if (response.isSuccess) {
        setTab3ActivityBoardItems(response.data);
      }
    } catch {
    } finally {
      setTab3Loading(false);
    }
  }, [
    arePermissionsInitialized,
    isActivityBoardItemUserScope,
    tab3SelectedBook,
    tab3SelectedUserId,
    tab3StartDate,
    tab3EndDate,
  ]);

  // Tab 3: Compute book summary rows
  const tab3BookSummaryRows = useMemo(
    () => buildTab3BookSummaryRows(tab3ActivityBoardItems, books, activityTypes),
    [tab3ActivityBoardItems, books, activityTypes]
  );

  // Tab 3: Grand total
  const tab3GrandTotal = useMemo(() => calculateTab3GrandTotal(tab3BookSummaryRows), [tab3BookSummaryRows]);

  // Tab 3: Chart data (only books with totalHours > 0)
  const tab3ChartData = useMemo(() => buildTab3ChartData(tab3BookSummaryRows), [tab3BookSummaryRows]);

  // Tab 3: DataGrid columns for master rows
  const tab3Columns: GridColDef<Tab3BookSummaryRow>[] = useMemo(
    () => [
      {
        field: 'bookTitle',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.projectName',
          defaultMessage: 'Proje Adı',
        }),
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'spentHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.spentHours',
          defaultMessage: 'Harcanan Süre (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 120,
      },
      {
        field: 'overtimeHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.overtimeHours',
          defaultMessage: 'Fazla Mesai (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
      {
        field: 'totalHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.totalHours',
          defaultMessage: 'Toplam (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
    ],
    [intl]
  );

  // Tab 3: DataGrid columns for detail (activity type) rows
  const tab3DetailColumns: GridColDef<Tab3ActivityTypeRow>[] = useMemo(
    () => [
      {
        field: 'activityTypeTitle',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.activityType',
          defaultMessage: 'Aktivite Türü',
        }),
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'spentHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.spentHours',
          defaultMessage: 'Harcanan Süre (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 120,
      },
      {
        field: 'overtimeHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.overtimeHours',
          defaultMessage: 'Fazla Mesai (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
      {
        field: 'totalHours',
        headerName: intl.formatMessage({
          id: 'activityMonitoring.totalHours',
          defaultMessage: 'Toplam (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        flex: 0.5,
        minWidth: 100,
      },
    ],
    [intl]
  );

  // Tab 3: Detail panel content (activity type breakdown per project)
  const getTab3DetailPanelContent = useCallback(
    (params: GridRowParams<Tab3BookSummaryRow>) => {
      const row = params.row;
      if (!row.activityTypeRows || row.activityTypeRows.length === 0) return null;

      return (
        <Box sx={{ p: 2 }}>
          <ZebraDataGrid<Tab3ActivityTypeRow>
            columns={tab3DetailColumns}
            rows={row.activityTypeRows}
            getRowId={(r) => r.activityTypeId}
            density="compact"
            hideFooter
            disableColumnMenu
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnReorder
            disableColumnSorting
            localeText={dataGridLocaleText}
          />
        </Box>
      );
    },
    [tab3DetailColumns, dataGridLocaleText]
  );

  // Tab 3: Detail panel height
  const getTab3DetailPanelHeight = useCallback((params: GridRowParams<Tab3BookSummaryRow>) => {
    const count = params.row.activityTypeRows?.length || 0;
    if (count === 0) return 0;
    return count * 36 + 56 + 16;
  }, []);

  // Tab 3: Pinned rows (grand total at bottom)
  const tab3PinnedRows = useMemo(
    () => ({
      bottom: [
        {
          bookId: -1,
          bookTitle: intl.formatMessage({
            id: 'activityMonitoring.grandTotal',
            defaultMessage: 'Genel Toplam',
          }),
          spentHours: tab3GrandTotal.spentHours,
          overtimeHours: tab3GrandTotal.overtimeHours,
          totalHours: tab3GrandTotal.totalHours,
          activityTypeRows: [],
        } as Tab3BookSummaryRow,
      ],
    }),
    [intl, tab3GrandTotal]
  );

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('get-activity-monitoring')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    getUsers();
    getActivityTypes();
    getBooks();
  }, []);

  useEffect(() => {
    if (isActivityBoardItemUserScope && tabValue !== 0 && tabValue !== 2) {
      setTabValue(0);
    }
  }, [isActivityBoardItemUserScope, tabValue]);

  // Fetch holidays based on date range
  useEffect(() => {
    const fetchHolidays = async () => {
      const yearsToFetch = new Set<number>();
      if (startDate) yearsToFetch.add(startDate.year());
      if (endDate) yearsToFetch.add(endDate.year());
      if (tab2StartDate) yearsToFetch.add(tab2StartDate.year());
      if (tab2EndDate) yearsToFetch.add(tab2EndDate.year());

      if (yearsToFetch.size === 0) return;

      try {
        const results: HolidayItem[] = [];
        for (const year of yearsToFetch) {
          const resp = await apiService.getHolidays(year);
          if (resp.isSuccess && resp.data) {
            results.push(...resp.data);
          }
        }
        setHolidays(results);
      } catch {}
    };

    fetchHolidays();
  }, [startDate, endDate, tab2StartDate, tab2EndDate]);

  useEffect(() => {
    getActivityBoardItems();
  }, [getActivityBoardItems]);

  useEffect(() => {
    getTab2ActivityBoardItems();
  }, [getTab2ActivityBoardItems]);

  useEffect(() => {
    getTab3ActivityBoardItems();
  }, [getTab3ActivityBoardItems]);

  return (
    <>
      <Paper
        sx={{
          borderRadius: '8px',
          width: { xs: '100vw', md: 'calc(100vw - 308px)' },
        }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '0.95rem',
            },
          }}>
          <Tab
            value={0}
            label={intl.formatMessage({
              id: 'activityMonitoring.tab1',
              defaultMessage: 'Kullanıcı Bazlı Özet',
            })}
          />
          {!isActivityBoardItemUserScope && (
            <Tab
              value={1}
              label={intl.formatMessage({
                id: 'activityMonitoring.tab2',
                defaultMessage: 'Aktivite Bazlı Özet',
              })}
            />
          )}
          <Tab
            value={2}
            label={intl.formatMessage({
              id: 'activityMonitoring.tab3',
              defaultMessage: 'Proje Bazlı Özet',
            })}
          />
        </Tabs>
      </Paper>

      {/* Tab 1 - User Summary */}
      <TabPanel value={tabValue} index={0}>
        {/* Filters */}
        <Grid container justifyContent="flex-start" spacing={1} sx={{ mb: 2 }}>
          {!isActivityBoardItemUserScope && (
            <Grid size={3}>
              <Autocomplete
                options={activityUsers}
                value={selectedUser}
                {...renderAutoCompleteLocaleText()}
                onChange={(_, newValue: User | null) => setSelectedUser(newValue)}
                getOptionLabel={(option: User) => option.nameSurname ?? ''}
                slotProps={{
                  paper: {
                    sx: {
                      backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900],
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={<FormattedMessage id="activityMonitoring.user" defaultMessage="Kullanıcı" />}
                    sx={{
                      '& .MuiButtonBase-root': {
                        border: 'none',
                        backgroundColor: 'inherit',
                        width: '2.25rem',
                        height: '2.25rem',
                        padding: '0.25rem',
                        [`& .${svgIconClasses.root}`]: {
                          fontSize: '1rem',
                        },
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                        },
                      },
                    }}
                  />
                )}
              />
            </Grid>
          )}
          <Grid size={2}>
            <DatePicker
              label={<FormattedMessage id="activityMonitoring.startDate" defaultMessage="Başlangıç Tarihi" />}
              localeText={renderDatePickerLocaleText()}
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                },
              }}
            />
          </Grid>
          <Grid size={2}>
            <DatePicker
              label={<FormattedMessage id="activityMonitoring.endDate" defaultMessage="Bitiş Tarihi" />}
              localeText={renderDatePickerLocaleText()}
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                },
              }}
            />
          </Grid>
          <Grid size={3} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body1" fontWeight={600} noWrap>
              <FormattedMessage id="activityMonitoring.expectedHours" defaultMessage="Seçilen Mesai Süresi" />
              :&nbsp;{displayedExpectedHours}
              &nbsp;
              <FormattedMessage id="activityMonitoring.hoursUnit" defaultMessage="saat" />
            </Typography>
          </Grid>
        </Grid>

        {/* DataGrid Table + Horizontal Bar Chart Side by Side */}
        <Grid container spacing={2}>
          <Grid size={isActivityBoardItemUserScope ? 12 : 6}>
            <Box
              sx={{
                height: '70vh',
                border: (t) => `1px ${t.palette.mode === 'dark' ? '#222833' : '#e6e8ed'} solid`,
                borderRadius: '8px',
              }}>
              <ZebraDataGrid<UserSummaryRow>
                pagination
                columns={columns}
                disableRowSelectionOnClick
                getRowId={(row) => row.id}
                loading={loading}
                localeText={renderDataGridLocaleText()}
                rows={userSummaryRows}
                getDetailPanelContent={getTab1DetailPanelContent}
                getDetailPanelHeight={getTab1DetailPanelHeight}
                initialState={{
                  aggregation: {
                    model: {
                      spentHours: 'sum',
                      totalHours: 'sum',
                    },
                  },
                }}
                slots={{
                  noRowsOverlay: CustomNoRowsOverlay,
                  toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
                }}
                slotProps={{
                  filterPanel: {
                    filterFormProps: {
                      logicOperatorInputProps: { variant: 'outlined', size: 'small' },
                      columnInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                      operatorInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                      valueInputProps: { InputComponentProps: { variant: 'outlined', size: 'small' } },
                    },
                  },
                }}
                disableAggregation={false}
                disableRowGrouping
              />
            </Box>
          </Grid>

          {!isActivityBoardItemUserScope && (
            <Grid size={6}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: '8px',
                  height: '70vh',
                  overflow: 'auto',
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    <FormattedMessage id="activityMonitoring.totalSpentHours" defaultMessage="Toplam Harcanan Süre" />
                    :&nbsp;{totalSpentHours}&nbsp;
                    <FormattedMessage id="activityMonitoring.hoursUnit" defaultMessage="saat" />
                  </Typography>
                </Box>

                {chartData.yLabels.length > 0 && (
                  <BarChart
                    layout="horizontal"
                    yAxis={[
                      {
                        scaleType: 'band',
                        data: chartData.yLabels,
                      },
                    ]}
                    series={[
                      {
                        data: chartData.values,
                        label: intl.formatMessage({
                          id: 'activityMonitoring.spentHours',
                          defaultMessage: 'Harcanan Süre (saat)',
                        }),
                        color: theme.palette.primary.main,
                      },
                    ]}
                    height={Math.max(300, chartData.yLabels.length * 40)}
                    margin={{ left: 150 }}
                  />
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Tab 2 - Activity Type Summary */}
      {!isActivityBoardItemUserScope && (
        <TabPanel value={tabValue} index={1}>
          {/* Filters */}
          <Grid container justifyContent="flex-start" spacing={1} sx={{ mb: 2 }}>
            <Grid size={3}>
              <Autocomplete
                options={activityUsers}
                value={tab2SelectedUser}
                {...renderAutoCompleteLocaleText()}
                onChange={(_, newValue: User | null) => setTab2SelectedUser(newValue)}
                getOptionLabel={(option: User) => option.nameSurname ?? ''}
                slotProps={{
                  paper: {
                    sx: {
                      backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900],
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={<FormattedMessage id="activityMonitoring.user" defaultMessage="Kullanıcı" />}
                    sx={{
                      '& .MuiButtonBase-root': {
                        border: 'none',
                        backgroundColor: 'inherit',
                        width: '2.25rem',
                        height: '2.25rem',
                        padding: '0.25rem',
                        [`& .${svgIconClasses.root}`]: {
                          fontSize: '1rem',
                        },
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                        },
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={2}>
              <DatePicker
                label={<FormattedMessage id="activityMonitoring.startDate" defaultMessage="Başlangıç Tarihi" />}
                localeText={renderDatePickerLocaleText()}
                value={tab2StartDate}
                onChange={(newValue) => setTab2StartDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                  },
                }}
              />
            </Grid>
            <Grid size={2}>
              <DatePicker
                label={<FormattedMessage id="activityMonitoring.endDate" defaultMessage="Bitiş Tarihi" />}
                localeText={renderDatePickerLocaleText()}
                value={tab2EndDate}
                onChange={(newValue) => setTab2EndDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Table + Chart Side by Side */}
          <Grid container spacing={2}>
            <Grid size={6}>
              <Box
                sx={{
                  height: '70vh',
                  border: (t) => `1px ${t.palette.mode === 'dark' ? '#222833' : '#e6e8ed'} solid`,
                  borderRadius: '8px',
                }}>
                <ZebraDataGrid<ActivityTypeSummaryRow>
                  pagination
                  columns={tab2Columns}
                  disableRowSelectionOnClick
                  getRowId={(row) => row.activityTypeId}
                  loading={tab2Loading}
                  localeText={dataGridLocaleText}
                  rows={activityTypeSummaryRows}
                  pinnedRows={tab2PinnedRows}
                  getDetailPanelContent={getTab2DetailPanelContent}
                  getDetailPanelHeight={getTab2DetailPanelHeight}
                  slots={{
                    noRowsOverlay: CustomNoRowsOverlay,
                    toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
                  }}
                  slotProps={{
                    filterPanel: {
                      filterFormProps: {
                        logicOperatorInputProps: { variant: 'outlined', size: 'small' },
                        columnInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                        operatorInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                        valueInputProps: { InputComponentProps: { variant: 'outlined', size: 'small' } },
                      },
                    },
                  }}
                  getRowClassName={(params) => (params.row.activityTypeId === -1 ? 'row--total' : '')}
                  disableRowGrouping
                />
              </Box>
            </Grid>

            <Grid size={6}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: '8px',
                  height: '70vh',
                  overflow: 'auto',
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    <FormattedMessage id="activityMonitoring.totalTime" defaultMessage="Toplam Süre" />
                    :&nbsp;{tab2GrandTotal.totalHours}&nbsp;
                    <FormattedMessage id="activityMonitoring.hoursUnit" defaultMessage="saat" />
                  </Typography>
                </Box>

                {tab2ChartData.yLabels.length > 0 && (
                  <BarChart
                    layout="horizontal"
                    yAxis={[
                      {
                        scaleType: 'band',
                        data: tab2ChartData.yLabels,
                      },
                    ]}
                    series={[
                      {
                        data: tab2ChartData.values,
                        label: intl.formatMessage({
                          id: 'activityMonitoring.totalTime',
                          defaultMessage: 'Toplam Süre',
                        }),
                        color: theme.palette.primary.main,
                      },
                    ]}
                    height={Math.max(300, tab2ChartData.yLabels.length * 40)}
                    margin={{ left: 150 }}
                  />
                )}
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
      )}

      {/* Tab 3 - Project Summary */}
      <TabPanel value={tabValue} index={2}>
        {/* Filters */}
        <Grid container justifyContent="flex-start" spacing={1} sx={{ mb: 2 }}>
          <Grid size={3}>
            <Autocomplete
              options={books}
              value={tab3SelectedBook}
              {...renderAutoCompleteLocaleText()}
              onChange={(_, newValue: ShortBook | null) => setTab3SelectedBook(newValue)}
              getOptionLabel={(option: ShortBook) => option.title ?? ''}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900],
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label={<FormattedMessage id="activityMonitoring.book" defaultMessage="Proje" />}
                  sx={{
                    '& .MuiButtonBase-root': {
                      border: 'none',
                      backgroundColor: 'inherit',
                      width: '2.25rem',
                      height: '2.25rem',
                      padding: '0.25rem',
                      [`& .${svgIconClasses.root}`]: {
                        fontSize: '1rem',
                      },
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                      },
                    },
                  }}
                />
              )}
            />
          </Grid>
          {!isActivityBoardItemUserScope && (
            <Grid size={3}>
              <Autocomplete
                options={activityUsers}
                value={tab3SelectedUser}
                {...renderAutoCompleteLocaleText()}
                onChange={(_, newValue: User | null) => setTab3SelectedUser(newValue)}
                getOptionLabel={(option: User) => option.nameSurname ?? ''}
                slotProps={{
                  paper: {
                    sx: {
                      backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900],
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={<FormattedMessage id="activityMonitoring.users" defaultMessage="Kullanıcılar" />}
                    sx={{
                      '& .MuiButtonBase-root': {
                        border: 'none',
                        backgroundColor: 'inherit',
                        width: '2.25rem',
                        height: '2.25rem',
                        padding: '0.25rem',
                        [`& .${svgIconClasses.root}`]: {
                          fontSize: '1rem',
                        },
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                        },
                      },
                    }}
                  />
                )}
              />
            </Grid>
          )}
          <Grid size={2}>
            <DatePicker
              label={<FormattedMessage id="activityMonitoring.startDate" defaultMessage="Başlangıç Tarihi" />}
              localeText={renderDatePickerLocaleText()}
              value={tab3StartDate}
              onChange={(newValue) => setTab3StartDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                },
              }}
            />
          </Grid>
          <Grid size={2}>
            <DatePicker
              label={<FormattedMessage id="activityMonitoring.endDate" defaultMessage="Bitiş Tarihi" />}
              localeText={renderDatePickerLocaleText()}
              value={tab3EndDate}
              onChange={(newValue) => setTab3EndDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'outlined',
                },
              }}
            />
          </Grid>
        </Grid>

        {/* Table + Chart Side by Side */}
        <Grid container spacing={2}>
          <Grid size={6}>
            <Box
              sx={{
                height: '70vh',
                border: (t) => `1px ${t.palette.mode === 'dark' ? '#222833' : '#e6e8ed'} solid`,
                borderRadius: '8px',
              }}>
              <ZebraDataGrid<Tab3BookSummaryRow>
                pagination
                columns={tab3Columns}
                disableRowSelectionOnClick
                getRowId={(row) => row.bookId}
                loading={tab3Loading}
                localeText={dataGridLocaleText}
                rows={tab3BookSummaryRows}
                pinnedRows={tab3PinnedRows}
                getDetailPanelContent={getTab3DetailPanelContent}
                getDetailPanelHeight={getTab3DetailPanelHeight}
                slots={{
                  noRowsOverlay: CustomNoRowsOverlay,
                  toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
                }}
                slotProps={{
                  filterPanel: {
                    filterFormProps: {
                      logicOperatorInputProps: { variant: 'outlined', size: 'small' },
                      columnInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                      operatorInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                      valueInputProps: { InputComponentProps: { variant: 'outlined', size: 'small' } },
                    },
                  },
                }}
                getRowClassName={(params) => (params.row.bookId === -1 ? 'row--total' : '')}
                disableRowGrouping
              />
            </Box>
          </Grid>

          <Grid size={6}>
            <Paper
              sx={{
                p: 2,
                borderRadius: '8px',
                height: '70vh',
                overflow: 'auto',
              }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  <FormattedMessage id="activityMonitoring.totalTime" defaultMessage="Toplam Süre" />
                  :&nbsp;{tab3GrandTotal.totalHours}&nbsp;
                  <FormattedMessage id="activityMonitoring.hoursUnit" defaultMessage="saat" />
                </Typography>
              </Box>

              {tab3ChartData.yLabels.length > 0 && (
                <BarChart
                  layout="horizontal"
                  yAxis={[
                    {
                      scaleType: 'band',
                      data: tab3ChartData.yLabels,
                    },
                  ]}
                  series={[
                    {
                      data: tab3ChartData.values,
                      label: intl.formatMessage({
                        id: 'activityMonitoring.totalTime',
                        defaultMessage: 'Toplam Süre',
                      }),
                      color: theme.palette.primary.main,
                    },
                  ]}
                  height={Math.max(300, tab3ChartData.yLabels.length * 40)}
                  margin={{ left: 150 }}
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>
    </>
  );
};

export default ActivityMonitoring;
